// Fix: v1 - 优化前端账号列表性能 - 2026-06-08
// 修复问题:
// 1. 使用React.memo包裹账号项组件,减少不必要的重新渲染
// 2. 优化事件处理函数,使用useCallback缓存
// 3. 添加稳定的key属性,避免列表渲染问题
// 4. 增强账号项组件的性能优化

import React, { useState, useEffect, useCallback, useRef, memo } from 'react'
import { Layout, Button, Modal, Form, Input, Select, message, Empty, Tag, Spin, Checkbox } from 'antd'
import {
  PlusOutlined, ReloadOutlined, DeleteOutlined, EditOutlined,
  ArrowUpOutlined, ArrowDownOutlined, VerticalAlignTopOutlined,
  DownOutlined, RightOutlined, SearchOutlined, FolderAddOutlined,
  CloseOutlined, LeftOutlined, RightOutlined as RightIcon, SwapOutlined,
  UserOutlined
} from '@ant-design/icons'
import api from '../api'

const { Content } = Layout
const CHROME_USER_AGENT = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/126.0.0.0 Safari/537.36'

const PLATFORM_COLORS = {
  douyin: '#ff4d4f', kuaishou: '#fa8c16', xiaohongshu: '#eb2f96',
  weibo: '#1890ff', bilibili: '#13c2c2', shipinhao: '#52c41a', jinritoutiao: '#722ed1'
}

const PLATFORM_NAMES = {
  douyin: '抖音', kuaishou: '快手', xiaohongshu: '小红书',
  weibo: '微博', bilibili: 'B站', shipinhao: '视频号', jinritoutiao: '头条'
}

const STATUS_CONFIG = {
  pending: { color: '#d9d9d9', label: '待扫码', dotBg: '#d9d9d9' },
  online:  { color: '#52c41a', label: '在线',   dotBg: '#52c41a' },
  offline: { color: '#faad14', label: '掉线',   dotBg: '#faad14' }
}

// Performance optimized account item component
const AccountItem = memo(function AccountItem({ 
  acc, 
  isSelected, 
  isChecked, 
  onSelect, 
  onContextMenu, 
  onToggleSelect 
}) {
  const isLoginSession = Boolean(acc.is_login_session)
  const isOnline = acc.status === 'online'
  const statusCfg = STATUS_CONFIG[acc.status] || STATUS_CONFIG.pending
  const displayName = acc.remark || acc.nickname || PLATFORM_NAMES[acc.platform] || '未命名'
  const subText = acc.sync_state === 'extracting' ? '正在同步资料...' : (acc.fans_text ? `粉丝 ${acc.fans_text}` : (acc.remark ? acc.nickname : ''))

  return (
    <div 
      onClick={() => { if (!isLoginSession) onSelect(acc) }}
      onContextMenu={(e) => { if (!isLoginSession) onContextMenu(e, acc) }}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 8px 28px',
        cursor: 'pointer',
        background: isSelected ? '#bae0ff' : 'transparent',
        borderLeft: isSelected ? '3px solid #1677ff' : '3px solid transparent',
        opacity: isOnline ? 1 : 0.6,
        transition: 'all 0.15s'
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f5f5f5' }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
    >
      {isLoginSession ? (
        <Spin size="small" style={{ flexShrink: 0 }} />
      ) : (
        <Checkbox 
          checked={isChecked} 
          onChange={() => onToggleSelect(acc.account_id)}
          onClick={e => e.stopPropagation()}
          style={{ flexShrink: 0 }} 
        />
      )}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%', overflow: 'hidden',
          background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `2px solid ${isOnline ? '#52c41a' : '#d9d9d9'}`
        }}>
          {acc.avatar_url ? (
            <img src={acc.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: isOnline ? 'none' : 'grayscale(100%)' }}
              onError={e => { e.target.style.display = 'none' }} />
          ) : (
            <UserOutlined style={{ color: isOnline ? '#8c8c8c' : '#bbb', fontSize: 18 }} />
          )}
        </div>
        <div style={{
          position: 'absolute', bottom: -2, right: -2,
          width: 16, height: 16, borderRadius: '50%',
          background: PLATFORM_COLORS[acc.platform] || '#999',
          border: '2px solid #fff', fontSize: 8, display: 'flex',
          alignItems: 'center', justifyContent: 'center', color: '#fff',
          fontWeight: 'bold'
        }}>
          {(PLATFORM_NAMES[acc.platform] || '?')[0]}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isOnline ? '#1a1a1a' : '#999' }}>
          {isLoginSession ? '正在登录...' : (acc.status === 'pending' ? '点击登录' : displayName)}
        </div>
        <div style={{ fontSize: 11, color: isOnline ? '#999' : '#bfbfbf', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {isLoginSession ? `${PLATFORM_NAMES[acc.platform] || '平台'} 授权中` : (acc.status === 'pending' ? '待扫码绑定' : (isOnline ? (subText || '粉丝 --') : '需要重新登录'))}
        </div>
      </div>

      <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: statusCfg.color }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusCfg.dotBg, display: 'inline-block' }} />
        {statusCfg.label}
      </span>
    </div>
  )
})

export default function AccountPage() {
  const [accounts, setAccounts] = useState([])
  const [groups, setGroups] = useState([])
  const [collapsedGroups, setCollapsedGroups] = useState({})
  const [selectedAccountId, setSelectedAccountId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [contextAccount, setContextAccount] = useState(null)
  const [contextMenuPos, setContextMenuPos] = useState(null)
  const [previewInfo, setPreviewInfo] = useState(null)
  const [currentUrl, setCurrentUrl] = useState('')
  const [webviewLoading, setWebviewLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [loginSessions, setLoginSessions] = useState([])

  const [addAccountOpen, setAddAccountOpen] = useState(false)
  const [addGroupOpen, setAddGroupOpen] = useState(false)
  const [editRemarkOpen, setEditRemarkOpen] = useState(false)
  const [changeGroupOpen, setChangeGroupOpen] = useState(false)
  const [editGroupOpen, setEditGroupOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [editingGroup, setEditingGroup] = useState(null)
  const [sidebarWidth, setSidebarWidth] = useState(290)
  const [dragging, setDragging] = useState(false)
  const sidebarRef = useRef(null)
  const [addAccountForm] = Form.useForm()
  const [addGroupForm] = Form.useForm()
  const [editRemarkForm] = Form.useForm()
  const [changeGroupForm] = Form.useForm()
  const [editGroupForm] = Form.useForm()

  const webviewRef = useRef(null)
  const heartbeatTimers = useRef({})
  const activeAccount = accounts.find(a => a.account_id === selectedAccountId)
  const isShipinhaoOffline = activeAccount?.platform === 'shipinhao' && activeAccount?.status === 'offline'
  const shipinhaoPublishUrl = 'https://channels.weixin.qq.com/platform'
  const effectivePreviewUrl = activeAccount?.platform === 'shipinhao'
    ? (previewInfo?.url || shipinhaoPublishUrl)
    : previewInfo?.url

  const loadWebviewUrl = useCallback((wv, url) => {
    if (!wv || !url) return
    wv.src = url
  }, [])

  const loadData = useCallback(async () => {
    try {
      const [accs, grps, cols] = await Promise.all([
        api.getAccounts(),
        api.getGroups(),
        api.getGroupCollapseState()
      ])
      setAccounts(Array.isArray(accs) ? accs : [])
      setGroups(Array.isArray(grps) ? grps : [])
      setCollapsedGroups(cols || {})
    } catch (err) {
      console.error('Load data error:', err)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData, refreshKey])

  useEffect(() => {
    const u1 = api.on('account-updated', (updated) => {
      if (updated?.account_id) {
        setAccounts(prev => prev.map(a => a.account_id === updated.account_id ? { ...a, ...updated } : a))
      }
    })
    const u2 = api.on('accounts-refreshed', () => setRefreshKey(k => k + 1))
    const u3 = api.on('login-session-updated', (session) => {
      if (!session?.account_id) return
      if (session.status === 'failed') message.error(session.error_message || '资料同步失败，请重新登录')
      setLoginSessions(prev => {
        if (session.status === 'completed' || session.status === 'failed') {
          return prev.filter(item => item.account_id !== session.account_id)
        }
        const exists = prev.some(item => item.account_id === session.account_id)
        return exists ? prev.map(item => item.account_id === session.account_id ? { ...item, ...session } : item) : [...prev, session]
      })
      if (session.status === 'completed') setRefreshKey(k => k + 1)
    })
    return () => { u1(); u2(); u3() }
  }, [])

  // When selected account becomes online, reload preview with creator center URL
  const prevStatusRef = useRef(null)
  useEffect(() => {
    const acc = accounts.find(a => a.account_id === selectedAccountId)
    const currentStatus = acc?.status || null
    if (prevStatusRef.current !== null && currentStatus === 'online' && prevStatusRef.current !== 'online') {
      // Status just transitioned to online — force preview reload
      api.getPreviewUrl(selectedAccountId).then(info => {
        if (info) {
          setPreviewInfo(info)
          setCurrentUrl(info.url || '')
        }
      })
    }
    prevStatusRef.current = currentStatus
  }, [accounts, selectedAccountId])

  // Heartbeat polling: every 60s check each online account's auth cookies
  useEffect(() => {
    const run = async () => {
      const onlineAccs = accounts.filter(a => a.status === 'online')
      if (onlineAccs.length === 0) return
      for (const acc of onlineAccs) {
        try {
          await api.heartbeat(acc.account_id)
        } catch (e) {}
      }
    }
    const id = setInterval(run, 60000)
    return () => clearInterval(id)
  }, [accounts])

  const handleSelectAccount = useCallback(async (account) => {
    setSelectedAccountId(account.account_id)
  }, [])

  useEffect(() => {
    if (!selectedAccountId) { setPreviewInfo(null); setCurrentUrl(''); return }
    let cancelled = false
    api.getPreviewUrl(selectedAccountId).then(info => {
      if (cancelled) return
      if (info) {
        setPreviewInfo(info)
        setCurrentUrl(info.url || '')
      }
    }).catch(() => {})
    return () => { cancelled = true }
  }, [selectedAccountId])

  useEffect(() => {
    const wv = webviewRef.current
    if (!wv || !effectivePreviewUrl || isShipinhaoOffline) return
    loadWebviewUrl(wv, effectivePreviewUrl)
  }, [selectedAccountId, effectivePreviewUrl, isShipinhaoOffline, loadWebviewUrl])

  useEffect(() => {
    const wv = webviewRef.current
    if (!wv) return

    const isBlockedExternalProtocol = (url = '') => {
      const value = String(url || '').trim()
      if (!value) return false
      if (/^(https?|wss?|about|data|blob|file|devtools):/i.test(value)) return false
      return /^[a-z][a-z0-9+.-]*:/i.test(value)
    }

    const onNavigate = (e) => {
      if (e.url) setCurrentUrl(e.url)
    }
    const onWillNavigate = (e) => {
      if (isBlockedExternalProtocol(e.url)) e.preventDefault()
    }
    const onNewWindow = (e) => {
      if (isBlockedExternalProtocol(e.url)) e.preventDefault()
    }
    const onDidStartLoading = () => setWebviewLoading(true)
    const onDidStopLoading = () => setWebviewLoading(false)
    const onDidFinishLoad = () => {
      setWebviewLoading(false)
      const loadedUrl = currentUrl || wv.src || ''
      setCurrentUrl(loadedUrl)
    }

    wv.addEventListener('did-navigate', onNavigate)
    wv.addEventListener('did-navigate-in-page', onNavigate)
    wv.addEventListener('will-navigate', onWillNavigate)
    wv.addEventListener('will-frame-navigate', onWillNavigate)
    wv.addEventListener('new-window', onNewWindow)
    wv.addEventListener('did-start-loading', onDidStartLoading)
    wv.addEventListener('did-stop-loading', onDidStopLoading)
    wv.addEventListener('did-finish-load', onDidFinishLoad)
    return () => {
      wv.removeEventListener('did-navigate', onNavigate)
      wv.removeEventListener('did-navigate-in-page', onNavigate)
      wv.removeEventListener('will-navigate', onWillNavigate)
      wv.removeEventListener('will-frame-navigate', onWillNavigate)
      wv.removeEventListener('new-window', onNewWindow)
      wv.removeEventListener('did-start-loading', onDidStartLoading)
      wv.removeEventListener('did-stop-loading', onDidStopLoading)
      wv.removeEventListener('did-finish-load', onDidFinishLoad)
    }
  }, [selectedAccountId, currentUrl])

  const handleDragStart = useCallback((e) => {
    e.preventDefault()
    setDragging(true)
  }, [])

  useEffect(() => {
    if (!dragging) return
    const onMove = (e) => {
      setSidebarWidth(prev => Math.max(200, Math.min(600, e.clientX)))
    }
    const onUp = () => setDragging(false)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [dragging])

  const handleDeleteAccount = useCallback((accountId) => {
    Modal.confirm({
      title: '删除账号', content: '确定删除该账号？',
      okText: '删除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        await api.deleteAccount(accountId)
        message.success('账号已删除')
        if (selectedAccountId === accountId) {
          setSelectedAccountId(null)
          setPreviewInfo(null)
          setCurrentUrl('')
        }
        setRefreshKey(k => k + 1)
      }
    })
  }, [selectedAccountId])

  const handleContextMenu = useCallback((e, account) => {
    e.preventDefault()
    setContextAccount(account)
    setContextMenuPos({ x: e.clientX, y: e.clientY })
  }, [])

  const closeContextMenu = useCallback(() => {
    setContextMenuPos(null)
    setContextAccount(null)
  }, [])

  const toggleSelect = useCallback((accountId) => {
    setSelectedIds(prev =>
      prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
    )
  }, [])

  useEffect(() => {
    const handler = () => { if (contextMenuPos) closeContextMenu() }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [contextMenuPos, closeContextMenu])

  const handleMove = async (accountId, direction) => {
    await api.moveAccount(accountId, direction)
    setRefreshKey(k => k + 1)
  }

  const handleAddAccount = async () => {
    try {
      const values = await addAccountForm.validateFields()
      if (!api.isElectron) { message.error('请在 Electron 窗口中操作'); return }
      const result = await api.beginAccountLogin({ platform: values.platform, group_id: values.group, remark: values.remark })
      if (result?.success === false) { message.error(result.error || '创建失败'); return }
      if (result) {
        setLoginSessions(prev => prev.some(item => item.account_id === result.account_id) ? prev.map(item => item.account_id === result.account_id ? { ...item, ...result } : item) : [...prev, result])
        message.success('已打开登录窗口，列表将显示正在登录状态')
        setAddAccountOpen(false)
        addAccountForm.resetFields()
      }
    } catch (err) {
      if (err.errorFields) return
      message.error(err.message || '创建失败')
    }
  }

  const handleEditRemark = async () => {
    try {
      const values = await editRemarkForm.validateFields()
      await api.updateAccount({ account_id: editingAccount.account_id, remark: values.remark })
      message.success('备注已更新')
      setEditRemarkOpen(false)
      setEditingAccount(null)
      setRefreshKey(k => k + 1)
    } catch (err) { if (err.errorFields) return }
  }

  const handleChangeGroup = async () => {
    try {
      const values = await changeGroupForm.validateFields()
      await api.updateAccount({ account_id: editingAccount.account_id, group_id: values.group_id })
      message.success('分组已更新')
      setChangeGroupOpen(false)
      setEditingAccount(null)
      setRefreshKey(k => k + 1)
    } catch (err) { if (err.errorFields) return }
  }

  const handleAddGroup = async () => {
    try {
      const values = await addGroupForm.validateFields()
      if (!api.isElectron) { message.error('请在 Electron 窗口中操作'); return }
      const result = await api.createGroup(values.name)
      if (result) {
        message.success('分组已创建')
        setAddGroupOpen(false)
        addGroupForm.resetFields()
        setRefreshKey(k => k + 1)
      }
    } catch (err) { if (err.errorFields) return }
  }

  const handleDeleteGroup = (groupId, groupName) => {
    Modal.confirm({
      title: '删除分组', content: `确定删除分组"${groupName}"？账号将移入默认分组。`,
      okText: '删除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        await api.deleteGroup(groupId)
        message.success('分组已删除')
        setRefreshKey(k => k + 1)
      }
    })
  }

  const handleEditGroup = async () => {
    try {
      const values = await editGroupForm.validateFields()
      await api.updateGroup({ group_id: editingGroup.group_id, group_name: values.name })
      message.success('分组已更新')
      setEditGroupOpen(false)
      setEditingGroup(null)
      setRefreshKey(k => k + 1)
    } catch (err) { if (err.errorFields) return }
  }

  const toggleGroup = async (groupId) => {
    const newState = !collapsedGroups[groupId]
    setCollapsedGroups(prev => ({ ...prev, [groupId]: newState }))
    await api.setGroupCollapse(groupId, newState)
  }

  const handleReLogin = (accountId) => {
    api.reLogin(accountId)
    message.info('登录窗口已打开，请完成登录后关闭窗口')
  }

  const handleRefreshAccounts = async () => {
    const onlineAccounts = accounts.filter(account => account.status === 'online' && account.fingerprint_id)
    const offlineAccounts = accounts.filter(account => account.status !== 'online' && account.fingerprint_id)
    if (onlineAccounts.length === 0 && offlineAccounts.length === 0) {
      setRefreshKey(k => k + 1)
      return
    }
    const hide = message.loading('正在同步账号资料...', 0)
    try {
      for (const account of onlineAccounts) await api.refreshProfile(account.account_id)
      for (const account of offlineAccounts) await api.heartbeat(account.account_id)
      setRefreshKey(k => k + 1)
      message.success('账号资料已刷新')
    } catch (err) {
      message.error(err.message || '资料刷新失败')
    } finally {
      hide()
      if (webviewRef.current && effectivePreviewUrl) webviewRef.current.src = effectivePreviewUrl
    }
  }

  // --- Multi-select ---

  const selectAllInGroup = (groupAccs) => {
    setSelectedIds(prev => {
      const gIds = groupAccs.filter(a => !a.is_login_session).map(a => a.account_id)
      const allSelected = gIds.every(id => prev.includes(id))
      return allSelected ? prev.filter(id => !gIds.includes(id)) : [...new Set([...prev, ...gIds])]
    })
  }

  const handleBatchDelete = () => {
    const count = selectedIds.length
    Modal.confirm({
      title: '批量删除', content: `确定删除选中的 ${count} 个账号？`,
      okText: '删除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        await api.batchDeleteAccounts(selectedIds)
        message.success(`已删除 ${count} 个账号`)
        setSelectedIds([])
        setRefreshKey(k => k + 1)
      }
    })
  }

  const clearSelection = () => setSelectedIds([])

  const handleBatchMove = async (gid) => {
    await api.batchMoveAccounts(selectedIds, gid)
    message.success(`已移动 ${selectedIds.length} 个账号`)
    setSelectedIds([])
    setRefreshKey(k => k + 1)
  }

  const handlePreviewReload = () => {
    if (webviewRef.current && effectivePreviewUrl) webviewRef.current.src = effectivePreviewUrl
  }

  const handlePreviewBack = () => {
    try {
      if (webviewRef.current?.canGoBack?.()) webviewRef.current.goBack()
    } catch (e) {}
  }

  const filteredAccounts = accounts.filter(a => {
    if (!searchText) return true
    const t = searchText.toLowerCase()
    return (a.nickname || '').toLowerCase().includes(t) ||
      (a.remark || '').toLowerCase().includes(t) ||
      (a.fans_text || '').toLowerCase().includes(t) ||
      (PLATFORM_NAMES[a.platform] || '').includes(searchText)
  })

  const filteredLoginSessions = loginSessions.filter(a => {
    if (!searchText) return true
    return (a.remark || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (PLATFORM_NAMES[a.platform] || '').includes(searchText)
  }).map(a => ({ ...a, is_login_session: true }))

  const groupedAccounts = {}
  groups.forEach(g => { groupedAccounts[g.group_id] = { group: g, accounts: [] } })
  ;[...filteredLoginSessions, ...filteredAccounts].forEach(a => {
    const gid = a.group_id || 'default'
    if (!groupedAccounts[gid]) groupedAccounts[gid] = { group: { group_id: gid, group_name: '未分组' }, accounts: [] }
    groupedAccounts[gid].accounts.push(a)
  })

  // Replace inline render with memoized component
  const renderAccountItem = (acc) => (
    <AccountItem
      key={acc.account_id}
      acc={acc}
      isSelected={selectedAccountId === acc.account_id}
      isChecked={selectedIds.includes(acc.account_id)}
      onSelect={handleSelectAccount}
      onContextMenu={handleContextMenu}
      onToggleSelect={toggleSelect}
    />
  )

  return (
    <Layout style={{ flex: 1, display: 'flex', flexDirection: 'row', position: 'relative' }}>
      <div ref={sidebarRef} style={{ width: sidebarWidth, minWidth: 200, maxWidth: 600, background: '#fafafa', borderRight: '1px solid #e8e8e8', overflow: 'hidden', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'relative' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e8e8e8' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Button type="primary" icon={<PlusOutlined />} block onClick={() => setAddAccountOpen(true)}>添加账号</Button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="small" icon={<ReloadOutlined />} onClick={handleRefreshAccounts}>刷新</Button>
            <Button size="small" icon={<FolderAddOutlined />} onClick={() => setAddGroupOpen(true)}>新建分组</Button>
          </div>
          <Input size="small" style={{ marginTop: 8 }} placeholder="搜索账号..." prefix={<SearchOutlined />}
            value={searchText} onChange={e => setSearchText(e.target.value)} allowClear />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {Object.values(groupedAccounts).length === 0 && (
            <Empty description="暂无账号，点击上方按钮添加" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
          )}
          {Object.values(groupedAccounts).map(({ group, accounts: groupAccs }) => (
            <div key={group.group_id} style={{ marginBottom: 4 }}>
              <div onClick={() => toggleGroup(group.group_id)}
                onContextMenu={(e) => {
                  e.preventDefault()
                  if (group.group_id === 'default') return
                  setEditingGroup(group)
                  editGroupForm.setFieldsValue({ name: group.group_name })
                  setEditGroupOpen(true)
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '6px 16px', cursor: 'pointer', userSelect: 'none',
                  background: '#f0f0f0', fontSize: 13, fontWeight: 600, color: '#555',
                  borderBottom: '1px solid #e8e8e8'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {collapsedGroups[group.group_id] ? <RightOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
                  {group.group_name}
                  {groupAccs.length > 0 && (
                    <Checkbox
                      checked={groupAccs.length > 0 && groupAccs.every(a => selectedIds.includes(a.account_id))}
                      indeterminate={selectedIds.length > 0 && groupAccs.some(a => selectedIds.includes(a.account_id)) && !groupAccs.every(a => selectedIds.includes(a.account_id))}
                      onChange={() => selectAllInGroup(groupAccs)}
                      onClick={e => e.stopPropagation()}
                      style={{ marginLeft: 4 }}
                    />
                  )}
                </span>
                <span style={{ fontSize: 11, color: '#999' }}>{groupAccs.length}</span>
              </div>

              {!collapsedGroups[group.group_id] && groupAccs.map(acc => renderAccountItem(acc))}
            </div>
          ))}
        </div>

        <div style={{ padding: '8px 16px', borderTop: '1px solid #e8e8e8', fontSize: 12 }}>
          {selectedIds.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: '#1677ff', fontWeight: 600, fontSize: 13 }}>已选 {selectedIds.length} 项</span>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={handleBatchDelete}>批量删除</Button>
              <Select
                size="small"
                placeholder="移动到分组"
                style={{ width: 130 }}
                value={undefined}
                onChange={(gid) => handleBatchMove(gid)}
              >
                {groups.map(g => (
                  <Select.Option key={g.group_id} value={g.group_id}>{g.group_name}</Select.Option>
                ))}
              </Select>
              <Button size="small" onClick={clearSelection}>取消选择</Button>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999' }}>
              <span>共 {accounts.length} 个账号</span>
              <Button type="link" size="small" onClick={() => {
                const allOpen = {}
                groups.forEach(g => { allOpen[g.group_id] = false })
                setCollapsedGroups(allOpen)
              }}>全部展开</Button>
            </div>
          )}
        </div>
      </div>
      <div
        onMouseDown={handleDragStart}
        style={{
          width: 5, cursor: 'col-resize', background: dragging ? '#1677ff' : 'transparent',
          transition: dragging ? 'none' : 'background 0.2s',
          flexShrink: 0, zIndex: 10
        }}
      />

      <Content style={{ display: 'flex', flexDirection: 'column', background: '#fff' }}>
        {selectedAccountId && activeAccount ? (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
              background: '#fafafa', borderBottom: '1px solid #e8e8e8', height: 36, flexShrink: 0
            }}>
              <Button type="text" size="small" icon={<LeftOutlined />} onClick={handlePreviewBack} />
              <Button type="text" size="small" icon={<RightIcon />} disabled />
              <Button type="text" size="small" icon={<ReloadOutlined />} onClick={handlePreviewReload} />
              <Tag color={PLATFORM_COLORS[activeAccount?.platform] || '#999'}
                style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: '0 4px', flexShrink: 0 }}>
                {PLATFORM_NAMES[activeAccount?.platform] || '?'}
              </Tag>
              <div style={{
                flex: 1, background: '#fff', borderRadius: 14, border: '1px solid #d9d9d9',
                padding: '2px 12px', fontSize: 12, color: '#999', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '22px',
                display: 'flex', alignItems: 'center', gap: 4
              }}>
                {webviewLoading && <Spin size="small" />}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUrl || '加载中...'}</span>
              </div>
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => {
                setSelectedAccountId(null)
                setPreviewInfo(null)
                setCurrentUrl('')
              }} />
            </div>

            <div style={{ flex: 1, position: 'relative', background: '#f5f5f5' }}>
              {previewInfo && activeAccount?.status !== 'pending' && !isShipinhaoOffline ? (
                <webview
                  ref={webviewRef}
                  key={`${selectedAccountId}-${previewInfo?.fingerprint_id || 'kw'}`}
                  partition={`persist:${previewInfo.fingerprint_id}`}
                  useragent={CHROME_USER_AGENT}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allowpopups="false"
                />
              ) : activeAccount?.status === 'pending' ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#faad14', flexDirection: 'column', gap: 12 }}>
                  <Spin size="large" />
                  <span style={{ fontSize: 14 }}>请在登录窗口中完成扫码登录</span>
                  <span style={{ fontSize: 12, color: '#999' }}>登录完成后请关闭登录窗口</span>
                </div>
              ) : activeAccount?.status === 'offline' ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#bfbfbf', flexDirection: 'column', gap: 8 }}>
                  <span style={{ fontSize: 14 }}>账号已掉线，请右键选择"重新登录"</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#bbb' }}>
                  加载中...
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#bbb' }}>
            <div style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }}>V</div>
            <div style={{ fontSize: 16 }}>请在左侧选择账号查看创作者中心</div>
          </div>
        )}
      </Content>

      {contextMenuPos && contextAccount && (
        <>
          <div onClick={closeContextMenu} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
          <ContextMenuUI x={contextMenuPos.x} y={contextMenuPos.y}
            onEditRemark={() => { closeContextMenu(); setEditingAccount(contextAccount); editRemarkForm.setFieldsValue({ remark: contextAccount.remark || '' }); setEditRemarkOpen(true) }}
            onChangeGroup={() => { closeContextMenu(); setEditingAccount(contextAccount); changeGroupForm.setFieldsValue({ group_id: contextAccount.group_id }); setChangeGroupOpen(true) }}
            onMoveTop={() => { closeContextMenu(); handleMove(contextAccount.account_id, 'top') }}
            onMoveUp={() => { closeContextMenu(); handleMove(contextAccount.account_id, 'up') }}
            onMoveDown={() => { closeContextMenu(); handleMove(contextAccount.account_id, 'down') }}
            onDelete={() => { closeContextMenu(); handleDeleteAccount(contextAccount.account_id) }}
            onReLogin={() => { closeContextMenu(); handleReLogin(contextAccount.account_id) }}
          />
        </>
      )}

      <Modal title="添加账号" open={addAccountOpen} onOk={handleAddAccount}
        onCancel={() => { setAddAccountOpen(false); addAccountForm.resetFields() }} okText="创建并登录" cancelText="取消">
        <Form form={addAccountForm} layout="vertical">
          <Form.Item name="platform" label="选择平台" rules={[{ required: true, message: '请选择平台' }]}>
            <Select placeholder="选择平台">
              {/* 如需恢复头条/微博选项，把下面 filter 去掉即可：Object.entries(PLATFORM_NAMES).map(...) */}
              {Object.entries(PLATFORM_NAMES)
                .filter(([k]) => !['jinritoutiao', 'weibo'].includes(k))
                .map(([k, v]) => (<Select.Option key={k} value={k}>{v}</Select.Option>))}
            </Select>
          </Form.Item>
          <Form.Item name="group" label="选择分组">
            <Select placeholder="默认分组" allowClear>
              {groups.map(g => <Select.Option key={g.group_id} value={g.group_id}>{g.group_name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input placeholder="可选备注" />
          </Form.Item>
          <div style={{ fontSize: 12, color: '#999', background: '#f6f8fa', padding: 8, borderRadius: 4 }}>
            点击创建后先显示“正在登录”，授权成功并同步资料后再生成真实账号卡片。
          </div>
        </Form>
      </Modal>

      <Modal title="新建分组" open={addGroupOpen} onOk={handleAddGroup}
        onCancel={() => { setAddGroupOpen(false); addGroupForm.resetFields() }}>
        <Form form={addGroupForm} layout="vertical">
          <Form.Item name="name" label="分组名称" rules={[{ required: true, message: '请输入分组名称' }]}>
            <Input placeholder="输入分组名称" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="修改备注" open={editRemarkOpen} onOk={handleEditRemark}
        onCancel={() => { setEditRemarkOpen(false); setEditingAccount(null) }}>
        <Form form={editRemarkForm} layout="vertical">
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="修改分组" open={changeGroupOpen} onOk={handleChangeGroup}
        onCancel={() => { setChangeGroupOpen(false); setEditingAccount(null) }}>
        <Form form={changeGroupForm} layout="vertical">
          <Form.Item name="group_id" label="目标分组" rules={[{ required: true }]}>
            <Select placeholder="选择分组">
              {groups.map(g => <Select.Option key={g.group_id} value={g.group_id}>{g.group_name}</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="编辑分组" open={editGroupOpen} onOk={handleEditGroup}
        onCancel={() => { setEditGroupOpen(false); setEditingGroup(null) }}>
        <Form form={editGroupForm} layout="vertical">
          <Form.Item name="name" label="分组名称" rules={[{ required: true }]}>
            <Input placeholder="分组名称" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}

function ContextMenuUI({ x, y, onEditRemark, onChangeGroup, onMoveTop, onMoveUp, onMoveDown, onDelete, onReLogin }) {
  const menuStyle = {
    position: 'fixed', zIndex: 1001, background: '#fff', borderRadius: 6,
    boxShadow: '0 3px 12px rgba(0,0,0,0.15)', padding: '4px 0', minWidth: 140,
    left: Math.min(x, window.innerWidth - 150), top: Math.min(y, window.innerHeight - 280)
  }

  const itemStyle = {
    padding: '6px 16px', cursor: 'pointer', fontSize: 13, display: 'flex',
    alignItems: 'center', gap: 8, color: '#333'
  }

  const Item = ({ icon, label, onClick, danger }) => (
    <div style={{ ...itemStyle, color: danger ? '#ff4d4f' : '#333' }} onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
      {icon} {label}
    </div>
  )

  return (
    <div style={menuStyle}>
      <Item icon={<EditOutlined />} label="修改备注" onClick={onEditRemark} />
      <Item icon={<SwapOutlined />} label="修改分组" onClick={onChangeGroup} />
      <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px 0' }} />
      <Item icon={<VerticalAlignTopOutlined />} label="置顶" onClick={onMoveTop} />
      <Item icon={<ArrowUpOutlined />} label="向上移动" onClick={onMoveUp} />
      <Item icon={<ArrowDownOutlined />} label="向下移动" onClick={onMoveDown} />
      <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px 0' }} />
      <Item icon={<ReloadOutlined />} label="重新登录" onClick={onReLogin} />
      <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px 0' }} />
      <Item icon={<DeleteOutlined />} label="删除账号" onClick={onDelete} danger />
    </div>
  )
}
